import { SongPreview } from '~/interfaces';
import { NoteIndex } from '~/modules/songs/utils/note-index';
import { calculateSongDifficulty } from '~/modules/songs/utils/song-difficulty';
import { VocalRange, vocalRangeFit } from '~/modules/songs/utils/vocal-range-fit';
import { ProfilePlay, scoreToPercentage } from '~/modules/user-profiles/profile-stats';

/** Why a song was recommended, shown to the player so the list does not feel arbitrary. */
export type RecommendationReason =
  | 'artist you sing'
  | 'genre you sing'
  | 'language you sing'
  | 'never sung'
  | 'room to improve'
  | 'fits your range'
  | 'right difficulty';

export interface Recommendation {
  song: SongPreview;
  score: number;
  reasons: RecommendationReason[];
}

const WEIGHTS: Record<RecommendationReason, number> = {
  // The strongest signal by a distance: singing an artist twice is a deliberate choice, while a
  // genre or language is often just what the song list happened to offer.
  'artist you sing': 40,
  'genre you sing': 15,
  'language you sing': 10,
  'never sung': 20,
  'room to improve': 25,
  'fits your range': 20,
  'right difficulty': 20,
};

/** Below this share of the maximum, a song is worth another go. */
const IMPROVABLE_BELOW = 0.7;

/** How many plays of an artist/genre/language it takes to count as a preference. */
const PREFERENCE_MIN_PLAYS = 1;

const normalise = (value: string) => value.trim().toLowerCase();

const countBy = <T>(items: T[], pick: (item: T) => string[] | undefined) => {
  const counts = new Map<string, number>();

  for (const item of items) {
    for (const value of pick(item) ?? []) {
      if (!value) continue;
      const key = normalise(value);
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }

  return counts;
};

export interface Taste {
  artists: Map<string, number>;
  genres: Map<string, number>;
  languages: Map<string, number>;
  /** Best score per song, to tell "not sung" from "sung badly". */
  bestPerSong: Map<string, number>;
  /** Measured by the vocal range detector, when the profile has run it. */
  vocalRange?: VocalRange;
  /**
   * The hardest chart this profile has actually sung well, as a difficulty score. Songs are
   * matched near it rather than to a fixed idea of skill, so it moves as they improve.
   */
  provenDifficulty?: number;
}

/** A song sung at least this well counts as one the singer can handle. */
const SUNG_WELL_ABOVE = 0.6;

/** How far from `provenDifficulty` still counts as a good match, on the 0-100 difficulty scale. */
const DIFFICULTY_TOLERANCE = 15;

/**
 * What a profile's plays say about it. Read from the songs they actually sang, so a profile with
 * no history simply has no preferences and gets discovery-led suggestions.
 */
export const tasteFromPlays = (
  plays: ProfilePlay[],
  songsById: Map<string, SongPreview>,
  notes: NoteIndex = {},
  vocalRange?: VocalRange,
): Taste => {
  const sungSongs = plays.map((play) => songsById.get(play.songKey)).filter((song): song is SongPreview => !!song);

  const bestPerSong = new Map<string, number>();
  for (const play of plays) {
    bestPerSong.set(play.songKey, Math.max(bestPerSong.get(play.songKey) ?? 0, play.score));
  }

  // The hardest chart they have actually sung well - not the hardest they have attempted, which
  // would push suggestions past what they can do.
  let provenDifficulty: number | undefined;
  for (const [songKey, best] of bestPerSong) {
    const entry = notes[songKey];
    if (!entry || scoreToPercentage(best) < SUNG_WELL_ABOVE) continue;

    const { score } = calculateSongDifficulty(entry);
    provenDifficulty = Math.max(provenDifficulty ?? 0, score);
  }

  return {
    artists: countBy(sungSongs, (song) => [song.artist]),
    genres: countBy(sungSongs, (song) => (song.genre ? [song.genre] : [])),
    languages: countBy(sungSongs, (song) => song.language),
    bestPerSong,
    vocalRange,
    provenDifficulty,
  };
};

const prefers = (counts: Map<string, number>, value: string | undefined) =>
  value !== undefined && (counts.get(normalise(value)) ?? 0) >= PREFERENCE_MIN_PLAYS;

/**
 * Scores one song for a profile. Every reason is measured: the taste ones from the songs actually
 * sung, and the range and difficulty ones from the song's own notes. A song the note index has
 * nothing on simply cannot earn the latter two, rather than being guessed at.
 */
export const scoreSong = (song: SongPreview, taste: Taste, notes: NoteIndex = {}): Recommendation | null => {
  const reasons: RecommendationReason[] = [];
  const best = taste.bestPerSong.get(song.id);

  if (best === undefined) {
    reasons.push('never sung');
  } else if (scoreToPercentage(best) < IMPROVABLE_BELOW) {
    reasons.push('room to improve');
  }

  if (prefers(taste.artists, song.artist)) reasons.push('artist you sing');
  if (prefers(taste.genres, song.genre)) reasons.push('genre you sing');
  if (song.language.some((language) => prefers(taste.languages, language))) reasons.push('language you sing');

  // Both of these need the song's notes; a song missing from the note index simply does not earn
  // them, rather than being guessed at.
  const entry = notes[song.id];
  if (entry) {
    if (taste.vocalRange && vocalRangeFit(entry, taste.vocalRange) === 'comfortable') {
      reasons.push('fits your range');
    }

    if (
      taste.provenDifficulty !== undefined &&
      Math.abs(calculateSongDifficulty(entry).score - taste.provenDifficulty) <= DIFFICULTY_TOLERANCE
    ) {
      reasons.push('right difficulty');
    }
  }

  // "Never sung" and "room to improve" describe most of the song list between them, so neither is
  // a recommendation on its own. Something has to tie the song to this singer: what they choose to
  // sing, what their voice covers, or what they have shown they can handle.
  const personalReasons: RecommendationReason[] = [
    'artist you sing',
    'genre you sing',
    'language you sing',
    'fits your range',
    'right difficulty',
  ];
  if (!reasons.some((reason) => personalReasons.includes(reason))) return null;

  return {
    song,
    score: reasons.reduce((total, reason) => total + WEIGHTS[reason], 0),
    reasons,
  };
};

export const recommendSongs = (
  songs: SongPreview[],
  taste: Taste,
  limit = 100,
  notes: NoteIndex = {},
): Recommendation[] =>
  songs
    .map((song) => scoreSong(song, taste, notes))
    .filter((recommendation): recommendation is Recommendation => recommendation !== null)
    .sort((a, b) => b.score - a.score || a.song.artist.localeCompare(b.song.artist))
    .slice(0, limit);
